import { Check, Folder, FolderOpen, Inbox, MoreHorizontal, Plus } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api.js";
import type { GroupSummary, JobGroup, Me } from "../types.js";

interface DraggedJob {
  id: string;
  title: string;
  company: string;
  groupId: string | null;
}

interface GroupContextValue extends GroupSummary {
  loaded: boolean;
  revision: number;
  draggedJob: DraggedJob | null;
  setDraggedJob: (job: DraggedJob | null) => void;
  createGroup: (name: string) => Promise<JobGroup>;
  moveJob: (jobId: string, groupId: string | null) => Promise<void>;
  openGroup: (groupId: string | null) => void;
  refreshGroups: () => Promise<void>;
}

const EMPTY_GROUPS: GroupSummary = { items: [], all_count: 0, ungrouped_count: 0 };
const GroupContext = createContext<GroupContextValue | null>(null);

export function useGroups(): GroupContextValue {
  const value = useContext(GroupContext);
  if (!value) throw new Error("useGroups must be used inside AppShell");
  return value;
}

export function AppShell({ me, children }: { me: Me; refreshMe: () => Promise<void>; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<GroupSummary>(EMPTY_GROUPS);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [revision, setRevision] = useState(0);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [menuGroupId, setMenuGroupId] = useState<string | null>(null);
  const [draggedJob, setDraggedJob] = useState<DraggedJob | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const groupRequestRef = useRef(0);
  const search = location.pathname === "/jobs" ? new URLSearchParams(location.search) : null;
  const activeGroup = search?.get("group") ?? null;
  const includeDismissed = search?.has("show_dismissed") ?? false;

  const refreshGroups = useCallback(async () => {
    const generation = ++groupRequestRef.current;
    try {
      const next = await api<GroupSummary>(
        `/api/v1/groups?include_dismissed=${includeDismissed ? "true" : "false"}`,
      );
      if (generation !== groupRequestRef.current) return;
      setSummary(next);
      setGroupsLoaded(true);
    } catch {
      if (generation !== groupRequestRef.current) return;
      setSummary(EMPTY_GROUPS);
      setGroupsLoaded(false);
    }
  }, [includeDismissed]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void refreshGroups(), 0);
    return () => window.clearTimeout(timeout);
  }, [refreshGroups, revision]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(""), 2400);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  function openGroup(groupId: string | null) {
    const params = new URLSearchParams(location.search);
    for (const key of [
      "q",
      "job",
      "location",
      "city",
      "region",
      "country",
      "workplace_type",
      "sort",
      "order",
      "view",
    ])
      params.delete(key);
    if (groupId) params.set("group", groupId);
    else params.delete("group");
    navigate(`/jobs${params.size ? `?${params.toString()}` : ""}`);
  }

  async function createGroup(name: string): Promise<JobGroup> {
    const group = await api<JobGroup>("/api/v1/groups", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    setRevision((value) => value + 1);
    return group;
  }

  async function submitGroup(event: FormEvent) {
    event.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      const group = await createGroup(newGroupName.trim());
      setNewGroupName("");
      setNewGroupOpen(false);
      setNotice(`已创建分组“${group.name}”`);
      openGroup(group.id);
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "无法创建分组");
    }
  }

  async function renameGroup(event: FormEvent, groupId: string) {
    event.preventDefault();
    if (!editingName.trim()) return;
    try {
      await api(`/api/v1/groups/${groupId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editingName.trim() }),
      });
      setEditingGroupId(null);
      setMenuGroupId(null);
      setRevision((value) => value + 1);
      setNotice("分组名称已更新");
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "无法重命名分组");
    }
  }

  async function deleteGroup(group: JobGroup) {
    if (!window.confirm(`删除“${group.name}”？其中职位会回到未分组。`)) return;
    try {
      await api(`/api/v1/groups/${group.id}`, { method: "DELETE" });
      setMenuGroupId(null);
      setRevision((value) => value + 1);
      setNotice(`已删除分组“${group.name}”`);
      if (activeGroup === group.id) openGroup("ungrouped");
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "无法删除分组");
    }
  }

  async function moveJob(jobId: string, groupId: string | null) {
    const target = groupId ? summary.items.find((group) => group.id === groupId)?.name : "未分组";
    try {
      await api(`/api/v1/jobs/${jobId}/group`, {
        method: "PUT",
        body: JSON.stringify({ group_id: groupId }),
      });
      setRevision((value) => value + 1);
      setNotice(`已移至${target ? `“${target}”` : "目标分组"}`);
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "无法移动职位");
      throw caught;
    } finally {
      setDraggedJob(null);
      setDropTarget(null);
    }
  }

  function acceptDrop(event: DragEvent, groupId: string | null) {
    event.preventDefault();
    const jobId = draggedJob?.id || event.dataTransfer.getData("application/x-roleworth-job");
    if (!jobId) {
      setDraggedJob(null);
      setDropTarget(null);
      return;
    }
    void moveJob(jobId, groupId).catch(() => undefined);
  }

  const context: GroupContextValue = {
    ...summary,
    loaded: groupsLoaded,
    revision,
    draggedJob,
    setDraggedJob,
    createGroup,
    moveJob,
    openGroup,
    refreshGroups,
  };

  return (
    <GroupContext.Provider value={context}>
      <div className="app-shell">
        <a className="skip-link" href="#main-content">
          跳到主要内容
        </a>
        <aside className="app-sidebar" aria-label="职位分组">
          <Link className="workspace-identity" to="/jobs">
            <span className="brand-tile" aria-hidden="true">
              R
            </span>
            <span>
              <strong>RoleWorth</strong>
              <small>{me.workspace.name}</small>
            </span>
          </Link>

          <nav className="group-navigation" aria-label="职位范围">
            <button
              className={`group-row system-group ${activeGroup == null ? "is-active" : ""}`}
              type="button"
              onClick={() => openGroup(null)}
            >
              <FolderOpen aria-hidden="true" />
              <span>ALL</span>
              <small>{summary.all_count}</small>
            </button>
            <button
              className={`group-row system-group ${activeGroup === "ungrouped" ? "is-active" : ""} ${dropTarget === "ungrouped" ? "is-drop-target" : ""}`}
              type="button"
              onClick={() => openGroup("ungrouped")}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDropTarget("ungrouped");
              }}
              onDragLeave={() => setDropTarget((value) => (value === "ungrouped" ? null : value))}
              onDrop={(event) => acceptDrop(event, null)}
            >
              <Inbox aria-hidden="true" />
              <span>未分组</span>
              <small>{summary.ungrouped_count}</small>
            </button>
          </nav>

          <section className="sidebar-section group-section" aria-labelledby="group-section-title">
            <div className="section-label">
              <span id="group-section-title">分组</span>
              <button
                className="sidebar-add-button"
                type="button"
                aria-label="创建新分组"
                onClick={() => setNewGroupOpen(true)}
              >
                <Plus aria-hidden="true" />
              </button>
            </div>
            <div className="custom-groups">
              {summary.items.map((group) =>
                editingGroupId === group.id ? (
                  <form
                    className="inline-group-form"
                    key={group.id}
                    onSubmit={(event) => renameGroup(event, group.id)}
                  >
                    <input
                      autoFocus
                      required
                      maxLength={60}
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setEditingGroupId(null);
                      }}
                    />
                    <button className="inline-group-submit" type="submit" aria-label="保存分组名称">
                      <Check aria-hidden="true" />
                    </button>
                  </form>
                ) : (
                  <div className="group-row-wrap" key={group.id}>
                    <button
                      className={`group-row ${activeGroup === group.id ? "is-active" : ""} ${dropTarget === group.id ? "is-drop-target" : ""}`}
                      type="button"
                      onClick={() => openGroup(group.id)}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        setDropTarget(group.id);
                      }}
                      onDragLeave={() => setDropTarget((value) => (value === group.id ? null : value))}
                      onDrop={(event) => acceptDrop(event, group.id)}
                    >
                      <Folder aria-hidden="true" />
                      <span>{group.name}</span>
                      <small>{group.job_count}</small>
                    </button>
                    <button
                      className="group-menu-trigger"
                      type="button"
                      aria-label={`管理分组 ${group.name}`}
                      onClick={() => setMenuGroupId((value) => (value === group.id ? null : group.id))}
                    >
                      <MoreHorizontal aria-hidden="true" />
                    </button>
                    {menuGroupId === group.id && (
                      <div className="group-menu">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroupId(group.id);
                            setEditingName(group.name);
                            setMenuGroupId(null);
                          }}
                        >
                          重命名
                        </button>
                        <button className="danger-text" type="button" onClick={() => void deleteGroup(group)}>
                          删除
                        </button>
                      </div>
                    )}
                  </div>
                ),
              )}
              {!summary.items.length && !newGroupOpen && <p className="sidebar-empty">创建分组来整理职位</p>}
            </div>
            {newGroupOpen && (
              <form className="inline-group-form new-group-form" onSubmit={submitGroup}>
                <input
                  autoFocus
                  required
                  maxLength={60}
                  placeholder="分组名称"
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setNewGroupOpen(false);
                      setNewGroupName("");
                    }
                  }}
                />
                <button className="inline-group-submit" type="submit" aria-label="创建分组">
                  <Check aria-hidden="true" />
                </button>
              </form>
            )}
          </section>
        </aside>
        <div className="app-content">{children}</div>
        <div className={`workspace-notice ${notice ? "is-visible" : ""}`} role="status" aria-live="polite">
          {notice}
        </div>
      </div>
    </GroupContext.Provider>
  );
}
